import type { Controller } from "../lifecycle";

const manualEvents: Array<keyof WindowEventMap> = [
  "wheel",
  "touchstart",
  "pointerdown",
  "keydown"
];

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export class GuidedTourController implements Controller {
  private root?: HTMLElement;
  private story?: HTMLElement;
  private toggle?: HTMLButtonElement;
  private speedButton?: HTMLButtonElement;
  private label?: HTMLElement;
  private status?: HTMLElement;
  private progress?: HTMLElement;
  private animationFrame = 0;
  private playing = false;
  private speed = 1;
  private startTime = 0;
  private startY = 0;
  private endY = 0;
  private startProgress = 0;
  private reducedMotion = false;

  private readonly onToggle = (): void => {
    if (this.playing) {
      this.pause("Tour paused.");
    } else {
      this.play();
    }
  };

  private readonly onSpeed = (): void => {
    this.speed = this.speed === 1 ? 2 : 1;
    if (this.speedButton) {
      this.speedButton.textContent = `${this.speed}×`;
      this.speedButton.setAttribute(
        "aria-label",
        `Tour speed ${this.speed}×`
      );
    }
    if (this.playing) {
      this.playing = false;
      cancelAnimationFrame(this.animationFrame);
      this.play();
      if (this.status) {
        this.status.textContent = `Guided tour continuing at ${this.speed} times speed.`;
      }
    }
  };

  private readonly onManualInput = (event: Event): void => {
    if (!this.playing) return;
    if (
      event.target instanceof Element &&
      event.target.closest("[data-guided-tour]")
    ) {
      return;
    }
    if (
      event instanceof KeyboardEvent &&
      !["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "Home", "End"].includes(
        event.key
      )
    ) {
      return;
    }
    this.pause("Tour paused by manual navigation.");
  };

  mount(root: ParentNode): void {
    this.root = root.querySelector<HTMLElement>("[data-guided-tour]") ?? undefined;
    this.story = root.querySelector<HTMLElement>("[data-film-story]") ?? undefined;
    this.toggle = root.querySelector<HTMLButtonElement>("[data-tour-toggle]") ?? undefined;
    this.speedButton =
      root.querySelector<HTMLButtonElement>("[data-tour-speed]") ?? undefined;
    this.label = root.querySelector<HTMLElement>("[data-tour-label]") ?? undefined;
    this.status = root.querySelector<HTMLElement>("[data-tour-status]") ?? undefined;
    this.progress = root.querySelector<HTMLElement>("[data-tour-progress]") ?? undefined;

    if (!this.root || !this.story || !this.toggle || !this.speedButton) return;

    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (this.reducedMotion) {
      this.toggle.disabled = true;
      if (this.label) this.label.textContent = "Tour off";
      if (this.status) {
        this.status.textContent =
          "Guided motion is disabled because reduced motion is preferred.";
      }
      return;
    }

    this.toggle.addEventListener("click", this.onToggle);
    this.speedButton.addEventListener("click", this.onSpeed);
    for (const eventName of manualEvents) {
      window.addEventListener(eventName, this.onManualInput, { passive: true });
    }
  }

  private play(): void {
    if (!this.story || !this.toggle) return;
    const storyRect = this.story.getBoundingClientRect();
    const storyTop = window.scrollY + storyRect.top;
    const storyBottom = storyTop + this.story.offsetHeight - window.innerHeight;
    const current = clamp(window.scrollY, storyTop, storyBottom);

    this.startY = current;
    this.endY = storyBottom;
    this.startProgress =
      storyBottom <= storyTop ? 0 : (current - storyTop) / (storyBottom - storyTop);
    this.startTime = performance.now();
    this.playing = true;
    this.toggle.setAttribute("aria-pressed", "true");
    if (this.label) this.label.textContent = "Pause tour";
    if (this.status) {
      this.status.textContent = `Guided tour playing at ${this.speed} times speed.`;
    }
    this.animationFrame = requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    if (!this.playing) return;

    const fullDuration = 20_000 / this.speed;
    const remainingFraction = 1 - this.startProgress;
    const duration = Math.max(800, fullDuration * remainingFraction);
    const progress = clamp((now - this.startTime) / duration, 0, 1);

    window.scrollTo(0, this.startY + (this.endY - this.startY) * progress);
    if (this.progress) {
      this.progress.style.transform = `scaleX(${progress})`;
    }

    if (progress >= 1) {
      this.pause("Guided tour complete.");
      return;
    }
    this.animationFrame = requestAnimationFrame(this.tick);
  };

  private pause(message: string): void {
    this.playing = false;
    cancelAnimationFrame(this.animationFrame);
    this.toggle?.setAttribute("aria-pressed", "false");
    if (this.label) this.label.textContent = "Guided tour";
    if (this.status) this.status.textContent = message;
  }

  destroy(): void {
    this.pause("");
    this.toggle?.removeEventListener("click", this.onToggle);
    this.speedButton?.removeEventListener("click", this.onSpeed);
    for (const eventName of manualEvents) {
      window.removeEventListener(eventName, this.onManualInput);
    }
    this.root = undefined;
    this.story = undefined;
    this.toggle = undefined;
    this.speedButton = undefined;
    this.label = undefined;
    this.status = undefined;
    this.progress = undefined;
  }
}
